import mustache from "mustache";
import {
  AplicacionResponse,
  fx_esMetodoManual,
  MetodoAplicacion,
} from "../../../../modulos/aplicaciones/response/AplicacionResponse";
import { formatMesDia } from "../../../../utils/fechaUtils";
import { _2d } from "../../../../utils/numberUtils";
import { BaseSecondSidebarItemRender } from "../../../base/sidebar/BaseSecondSidebarItemRender";
import { SidebarExpandedStatsRender } from "../../../implementations/sidebar/SidebarExpandedStatsRender";
import template from "./aplicacionSidebarItemContentTemplate.html";

mustache.parse(template);

/** Contador para ids únicos por instancia (botones del contenido expandido). */
let APLICACION_ITEM_ID = 0;

/** Nombre visible de cada método, para el título y la etiqueta del item. */
const NOMBRE_METODO: Record<MetodoAplicacion, string> = {
  dron: "Dron",
  estacionaria: "Estacionaria",
  espalda: "Espalda",
  tractor: "Tractor",
  drench: "Drench",
};

/** Item del sidebar de aplicaciones: label con la fecha y el **método**
 *  ("Abril 12 · Espalda"), título cultivo/tratamiento ("CAÑA / BROCA") y
 *  contenido con la grilla de stats (área, volumen, descarga/ha) y las dosis de
 *  los productos aplicados, con el botón editar en el subheader. Al
 *  seleccionarse notifica la aplicación (el mapa la pinta).
 *
 *  El método va en el label desde que **una finca puede tener varias
 *  aplicaciones el mismo día** (2026-10-15): con la fecha sola, cinco
 *  aplicaciones de un martes se veían como cinco filas idénticas. Lo que las
 *  distingue es el método, el responsable y los lotes — por eso el responsable
 *  y los lotes aplicados están en el contenido expandido. */
export class AplicacionSidebarItemRender extends BaseSecondSidebarItemRender {
  /** Id único de la instancia (para los ids del contenido expandido). */
  private readonly _aplicacionItemId: number;

  /**
   * @param _aplicacion   Aplicación del item.
   * @param _onAplicacion Notifica la aplicación al seleccionarse el item.
   * @param _onEdit       Abre la edición de la aplicación (botón ✏).
   * @param _onDelete     Elimina la aplicación (botón 🗑, con confirmación).
   */
  constructor(
    private readonly _aplicacion: AplicacionResponse,
    private readonly _onAplicacion: (aplicacion: AplicacionResponse) => void,
    private readonly _onEdit: (aplicacion: AplicacionResponse) => void,
    private readonly _onDelete: (aplicacion: AplicacionResponse) => void,
  ) {
    super();
    APLICACION_ITEM_ID += 1;
    this._aplicacionItemId = APLICACION_ITEM_ID;
  }

  /** Fecha y método (p. ej. "Abril 12 · Espalda"). */
  protected getLabel(): string {
    const fecha = formatMesDia(this._aplicacion.fechaAplicacion);
    return `${fecha} · ${NOMBRE_METODO[this._aplicacion.metodo]}`;
  }

  /** Cultivo / tratamiento (p. ej. "CAÑA / BROCA"). */
  protected getTitle(): string {
    const partes = [
      this._aplicacion.cultivoNombre,
      this._aplicacion.cultivoTratamientoNombre,
    ].filter((parte) => parte != null && parte !== "");
    return partes.length > 0 ? partes.join(" / ") : "Aplicación";
  }

  /** Stats de la aplicación + dosis por hectárea (productos o mensaje vacío). */
  protected getItemContent(): string {
    const aplicacion = this._aplicacion;
    const descargaHa =
      aplicacion.descargaHa ??
      (aplicacion.taskArea > 0
        ? aplicacion.totalAmount / aplicacion.taskArea
        : 0);

    const stats = new SidebarExpandedStatsRender([
      { value: _2d(aplicacion.taskArea), label: "Área Aplicada Has" },
      { value: _2d(aplicacion.totalAmount), label: "Total Volumen (L/Kg)" },
      { value: _2d(descargaHa), label: "Descarga / Ha (L/Kg)" },
    ]);

    const esManual = fx_esMetodoManual(aplicacion.metodo);

    return mustache.render(template, {
      id: this._aplicacionItemId,
      stats: stats.render(),
      // La dosis cambia de denominador con el método: el rótulo tiene que
      // seguirla o estaría diciendo "por hectárea" sobre un número que es por
      // caneca.
      etiquetaDosis: esManual ? "Dosis x Recipiente" : "Dosis x Hectárea",
      responsable: esManual ? aplicacion.responsable : null,
      // El equipo solo existe en estacionaria y tractor; la bomba, en espalda.
      equipo: aplicacion.equipo,
      recipiente:
        esManual && aplicacion.recipienteNombre
          ? `${aplicacion.recipienteNombre} · ${aplicacion.volumenRecipienteLitros ?? 0} L`
          : null,
      lotes: aplicacion.lotes.map((lote) => ({
        nombre: lote.loteNombre,
        cantidad:
          aplicacion.metodo === "drench"
            ? `${_2d(lote.plantas ?? 0)} plantas`
            : _2d(lote.cantidad ?? 0),
      })),
      detalles: aplicacion.detalles.map((detalle) => ({
        productoDescripcion: detalle.productoDescripcion,
        dosis: _2d(detalle.dosis),
        unidadMedida: detalle.unidadMedida,
      })),
    });
  }

  /** Cablea los botones editar/eliminar del contenido expandido. */
  protected connectExpanded(): void {
    this._editBtn = document.getElementById(
      `ap-item-edit-${this._aplicacionItemId}`,
    );
    this._deleteBtn = document.getElementById(
      `ap-item-delete-${this._aplicacionItemId}`,
    );
    this._editBtn?.addEventListener("click", this._onEditClick);
    this._deleteBtn?.addEventListener("click", this._onDeleteClick);
  }

  /** Libera los botones al colapsar/disponer. */
  protected disposeExpanded(): void {
    this._editBtn?.removeEventListener("click", this._onEditClick);
    this._deleteBtn?.removeEventListener("click", this._onDeleteClick);
    this._editBtn = null;
    this._deleteBtn = null;
  }

  /** Al seleccionarse: notifica la aplicación (el mapa la pinta). */
  protected onSelected(): void {
    this._onAplicacion(this._aplicacion);
  }

  /** Botón editar (disponible mientras el item está expandido). */
  private _editBtn: HTMLElement | null = null;
  /** Botón eliminar (disponible mientras el item está expandido). */
  private _deleteBtn: HTMLElement | null = null;

  private _onEditClick = (): void => this._onEdit(this._aplicacion);
  private _onDeleteClick = (): void => this._onDelete(this._aplicacion);
}
